import { defaults, map } from 'es-toolkit/compat';
import { type FC, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Field } from 'react-final-form';
import { createUniqueElementId } from '../../app.util';
import { FormLabelWithOptional } from './FormLabelWithOptional';
import type { CommonFormInputProps, FormGroupProps } from './form.types';
import type { TagSelectProps } from './TagSelect';

export interface JFormMultiselectProps extends CommonFormInputProps, FormGroupProps {
  children?: (props: TagSelectProps) => JSX.Element;
  number?: boolean; // if you want the values to be parsed as numbers instead of strings
  onChange?: () => void;
  options?: string[];
  placeholder?: string;
}

export const JFormMultiselect: FC<JFormMultiselectProps> = ({
  children,
  className,
  label,
  name,
  number,
  onChange,
  optional,
  options,
  validate,
  ...inputProps
}) => {
  const inputId = useMemo(() => createUniqueElementId('checkbox'), []);
  const defaultInputProps = defaults(inputProps, {
    autoComplete: 'off',
  });
  const optionObjs = useMemo(
    () =>
      options?.map((opt) => ({
        label: opt,
        value: opt,
      })),
    [
      options,
    ],
  );

  return (
    <Form.Group className={className} controlId={inputId} data-cy={`multiselect-${name}`}>
      <FormLabelWithOptional htmlFor={inputId} label={label && (typeof label === 'string' ? label : label())} optional={optional} />
      <Field
        format={(items) =>
          map(items, (item) => ({
            label: item,
            value: item,
          }))
        }
        name={name}
        parse={(items) => map(items, (item) => (number ? parseInt(item.value, 10) : item.value))}
        validate={validate}
      >
        {(fieldProps) =>
          children?.({
            defaultValue: [],
            errors: fieldProps.meta.error
              ? [
                  fieldProps.meta.error,
                ]
              : undefined,
            inputId,
            invalid: Boolean(fieldProps.meta.invalid),
            options: optionObjs,
            ...fieldProps.input,
            ...defaultInputProps,
            onChange: (value) => {
              onChange?.();
              fieldProps.input.onChange(value);
            },
          })
        }
      </Field>
    </Form.Group>
  );
};
